/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logDemoCustomContent } from 'src/core/services/telemetry/eventsNamesTemplate';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { DemoHeader } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoHeader';
import { DemoPageAgreement } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoPageAgreement';
import { DocumentInputStep } from 'src/languageStudio/appShell/prebuiltDemos/components/DocumentInputStep';
import { NextStepsStep } from 'src/languageStudio/appShell/prebuiltDemos/components/NextStepsStep';
import { ResultsStep } from 'src/languageStudio/appShell/prebuiltDemos/components/ResultsStep';
import { VisualizedSentimentResult } from 'src/languageStudio/appShell/prebuiltDemos/sentimentDemo/VisualizedSentimentResult';
import { commonDemosMessages } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';
import { labelDescriptionIconClassName } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonStyles';
import { sentimentDemoSamples } from 'src/languageStudio/appShell/prebuiltDemos/utils/demoSamples';
import {
    sentimentApiReferenceLink,
    sentimentDocLink,
    sentimentOverviewLink,
    sentimentSamplesLink,
    sentimentSdkLink,
    textAnalyticsPricingLink
} from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportLinks';
import { Language, sentimentDemoSupportedLanguages } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedLanguages';
import { useTextAnalyticsCapabilities } from 'src/languageStudio/core/hooks/useTextAnalyticsCapabilities';
import { SentimentDocuments } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';
import {
    isDocumentSwaggerRtl,
    isDocumentSwaggerWordTokenized,
    validateLanguageStudioResource
} from 'src/languageStudio/core/utils/demosUtils';

const messages = defineMessages({
    pageHeader: {
        id: 'languageStudio.sentimentDemoPage.pageHeader',
        defaultMessage: 'Analyze sentiment and opinions'
    },
    capabilityDescription: {
        id: 'languageStudio.sentimentDemoPage.capabilityDescription',
        defaultMessage: `The Text Analytics API's Sentiment Analysis feature provides sentiment labels
         (such as "negative", "neutral" and "positive") and confidence scores at the sentence and
         document-level. You can also send Opinion Mining requests using the Sentiment Analysis endpoint,
         which provides granular information about the opinions related to words (such as the attributes of
         products or services) in the text.`
    },
    emptyState: {
        id: 'languageStudio.sentimentDemoPage.emptyState',
        defaultMessage: `Your results will appear here once you enter or upload some text and
         press Run. The file format needs to be .txt`
    },
    assessmentMiningOptionLabel: {
        id: 'languageStudio.sentimentDemoPage.assessmentMiningOptionLabel',
        defaultMessage: 'Enable opinion mining'
    },
    assessmentMiningDescription: {
        id: 'languageStudio.sentimentDemoPage.assessmentMiningDescription',
        defaultMessage: 'Opinion Mining is an extension of Sentiment Analysis which gives you actionable insights.'
    }
});

/**
 * Displays the Sentiment demo page.
 */
export const SentimentDemoPage = observer(() => {
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { isSignedIn } = rootStore.authStore;
    const { csAccountStore } = rootStore.azureStore.session;
    const { tracker, sentiment, getSentiment, resetResults, getCurlCommand, detectedLanguages } = useTextAnalyticsCapabilities();

    const [document, setDocument] = React.useState('');
    const [language, setLanguage] = React.useState<Language['code']>('en');
    const [withAssessmentMining, setWithAssessmentMining] = React.useState(true);
    const [languageAutoDetection, setLanguageAutoDetection] = React.useState(false);
    const [selectedSampleTitle, setSelectedSampleTitle] = React.useState<string>(null);

    const handleDocumentChange = (newDocument: string) => {
        if (sentiment) {
            resetResults();
        }
        setDocument(newDocument);
    };

    const handleLanguageChange = (code: Language['code']) => {
        if (!sentimentDemoSupportedLanguages.find(lang => lang.code === code).isOpinionMiningSupported) {
            setWithAssessmentMining(false);
        }

        setLanguage(code);
    };

    const sentimentOptions = (
        <Stack styles={{ root: { flex: 2 } }} tokens={{ childrenGap: 17 }}>
            <LuisText fontWeight={600}>{localize(commonDemosMessages.options)}</LuisText>

            <Toggle
                inlineLabel
                checked={withAssessmentMining}
                onText={localize(commonDemosMessages.onText)}
                offText={localize(commonDemosMessages.offText)}
                onChange={(_, isChecked) => setWithAssessmentMining(isChecked)}
                disabled={
                    isSignedIn &&
                    (!sentimentDemoSupportedLanguages.find(lang => lang.code === language).isOpinionMiningSupported ||
                        !validateLanguageStudioResource({ resource: csAccountStore, localize }).isValid)
                }
                label={
                    <Stack horizontal verticalAlign="center" tokens={{ padding: '0 0 5px 0' }}>
                        <LuisText variant="medium" fontWeight={500} styles={{ root: { whiteSpace: 'nowrap' } }}>
                            {localize(messages.assessmentMiningOptionLabel)}
                        </LuisText>
                        <CalloutButton
                            isDarkMode={false}
                            iconClassName={labelDescriptionIconClassName}
                            content={localize(messages.assessmentMiningDescription)}
                            ariaLabel={localize(messages.assessmentMiningDescription)}
                        />
                    </Stack>
                }
            />
        </Stack>
    );

    const handleRunDemo = () => {
        if (!selectedSampleTitle) {
            logDemoCustomContent('Analyze Sentiment');
        }

        getSentiment({
            document,
            language,
            withAssessmentMining,
            sampleTitle: selectedSampleTitle,
            autoDetectLanguage: languageAutoDetection
        });
    };

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <DemoHeader
                featureName="Analyze Sentiment"
                demoVersion={LuisConstants.Demos.ApiVersions.GA}
                getSdkLink={sentimentSdkLink}
                learnMoreLink={sentimentOverviewLink}
                documentationLink={sentimentDocLink}
                supportedPlatforms={['docker', 'cloud']}
                demoTitle={localize(messages.pageHeader)}
                gitHubSamplesLink={sentimentSamplesLink}
                demoDescription={localize(messages.capabilityDescription)}
            />

            <DocumentInputStep
                document={document}
                selectedLanguage={language}
                featureName="Analyze Sentiment"
                samplesList={sentimentDemoSamples}
                acceptedResourceType="TextAnalytics"
                onLanguageChange={handleLanguageChange}
                onDocumentChange={handleDocumentChange}
                selectedSampleTitle={selectedSampleTitle}
                languagesList={sentimentDemoSupportedLanguages}
                supportedRegionLink={sentimentApiReferenceLink}
                onRenderHorizontalOptions={() => sentimentOptions}
                containerStackProps={{ tokens: { maxWidth: '100%' } }}
                onLanguageAutoDetectionClick={setLanguageAutoDetection}
                onSampleClick={sample => setSelectedSampleTitle(sample?.title)}
                isTextRTL={isDocumentSwaggerRtl(detectedLanguages?.documents[0].detectedLanguage.iso6391Name ?? language)}>
                <DemoPageAgreement
                    onRunDemoClick={handleRunDemo}
                    featureName="Analyze Sentiment"
                    isRunDisabled={!document.length}
                    acceptedResourceType="TextAnalytics"
                    pricingLink={textAnalyticsPricingLink}
                />
            </DocumentInputStep>

            <ResultsStep<SentimentDocuments>
                results={sentiment}
                isLoading={tracker.isRunning}
                featureName="Analyze Sentiment"
                emptyStateMessage={localize(messages.emptyState)}>
                <VisualizedSentimentResult
                    document={document}
                    sentimentResult={sentiment}
                    detectedLanguage={detectedLanguages}
                    isAssessmentMiningActive={withAssessmentMining}
                    isLabelerRTL={isDocumentSwaggerRtl(detectedLanguages?.documents[0].detectedLanguage.iso6391Name ?? language)}
                    isLabelerWordTokenized={isDocumentSwaggerWordTokenized(
                        detectedLanguages?.documents[0].detectedLanguage.iso6391Name ?? language
                    )}
                />
            </ResultsStep>

            <NextStepsStep
                sdkLink={sentimentSdkLink}
                isResultsEmpty={!sentiment}
                featureName="Analyze Sentiment"
                documentationLink={sentimentDocLink}
                sampleCodeLink={sentimentSamplesLink}
                demosToDisplay={['detectLanguage', 'ner', 'pii', 'keyPhrases']}
                curlCommand={getCurlCommand({
                    language,
                    text: document,
                    languageAutoDetection,
                    endpoint: 'SentimentAnalysis',
                    parameters: { opinionMining: withAssessmentMining }
                })}
            />
        </Stack>
    );
});
